import { useCart } from '@shared/cart-react';
import { Button, Drawer, Icon, Select, SmoothMount } from '@shared/cove-ui';
import { Product as ProductType, useProducts } from '@shared/react-product';
import { useRouter } from '@shared/react-router';
import {
  PromoCodeBreakdown,
  PromoCodeInput,
} from '@shared/react-ui-core/stories/Components/CartTotalsWidget';
import PrettyPrice from '@shared/react-ui-core/stories/Components/PrettyPrice';
import isEmpty from 'lodash/isEmpty';
import { useContext, useEffect, useRef } from 'react';
import { BaseContext } from '../../context/base';
import { localStore } from '../../libs/storage';
import { getDate, isMobile } from '../../libs/utils';
import { roundTo } from '../../utils';
import analytics from '../../utils/analytics';
import orderedProductsById from '../../utils/orderedProductsById';

const NON_SENSORS = ['Yi-IC'];

const getOptions = (min: number, max: number) => {
  let options = [];
  for (let i = min; i < max + 1; i++) {
    options.push({ value: i, label: i });
  }
  return options;
};

const Product = ({
  product,
  handleUpdate,
  isStatic,
  isFree,
}: {
  product: ProductType;
  handleUpdate: (item: { sku: string; qty: number }) => void;
  isStatic: boolean;
  isFree: boolean;
}) => {
  const cart = useCart();
  const refQuantityInput = useRef<HTMLDivElement | null>(null);
  const cartProduct = cart?.cartProducts?.[product?.sku];
  const productDiscounts = cart?.calculateProductDiscounts?.(product.sku);
  const salePrice = parseFloat(productDiscounts?.salePrice || '0');
  const onSale = product.price > salePrice || !salePrice;
  const minimumQtyMap = { 'CPA-PANEL-345-1': 1 };
  const minQty = minimumQtyMap[product.sku as keyof typeof minimumQtyMap] || 0;
  const options = getOptions(minQty, product.maximumSensorCountOnSite);

  const getAccessiblePrice = (product: ProductType) => {
    let accessibleText = `$${product.price} each`;
    if (onSale && !isFree) {
      accessibleText = `$${salePrice} each, regularly $${roundTo(
        `${product.price}`,
      )}`;
    }
    if (isFree) {
      accessibleText = `1 free, $${salePrice} for each additional`;
    }
    return accessibleText;
  };

  return (
    <div className="border-b border-b-cove-dove" data-test-id="cart-product">
      <div className="flex items-center">
        <img
          className="mr-cove-5 w-full max-w-[40px] object-contain"
          src={product.primaryImage.url}
          alt={product.name}
        />
        <div className="flex w-full flex-col py-[15px]">
          <div>
            <div>
              <p className="mb-cove-5 flex font-medium">{product.name}</p>
              <div className="flex pr-[11px]" aria-hidden="true">
                {onSale ? (
                  <span className=" relative mb-0 mr-cove-5 whitespace-nowrap text-base before:absolute before:top-1/2 before:h-px before:w-full before:translate-x-0 before:translate-y-[calc(-50%-2px)] before:bg-black before:content-[''] sm:mr-cove-15">
                    ${roundTo(product.price.toString())}
                    {!isStatic && (
                      <span className="text-xs leading-[14px]">/ea</span>
                    )}
                  </span>
                ) : (
                  <span />
                )}
                <span className="mb-0 whitespace-nowrap font-medium">
                  {isFree ? '1 Free ' : `$${roundTo(salePrice.toString())}`}
                  {!isStatic && !isFree && (
                    <span className=" text-xs leading-[14px]">/ea</span>
                  )}
                </span>
              </div>
              <p className="visually-hidden" aria-label="Price">
                {getAccessiblePrice(product)}
              </p>
              {!isStatic && !minQty && (
                <button
                  className="b order-0 ml-0
                  cursor-pointer bg-transparent p-0 text-xs text-cove-blue"
                  onClick={() => handleUpdate({ sku: product.sku, qty: 0 })}
                >
                  Remove
                  <span className="sr-only">{product.name} from cart</span>
                </button>
              )}
              {!isStatic && isFree && (
                <span
                  className="ml-cove-10 text-xs font-semibold leading-[14px]"
                  aria-hidden="true"
                >
                  (${salePrice}/ea additional camera)
                </span>
              )}
            </div>
          </div>
        </div>
        {!isStatic && (
          <div>
            <Select
              name={`product-select-${product.sku}`}
              value={`${cartProduct?.qty || 0}`}
              defaultValue={`${cartProduct?.qty || 0}`}
              onChange={(value) => {
                handleUpdate({ sku: product.sku, qty: parseInt(value) });
              }}
              align="start"
            >
              {options.map((option) => {
                return (
                  <Select.Option key={option.value} value={`${option.value}`}>
                    {option.label}
                  </Select.Option>
                );
              })}
            </Select>

            <div
              className=" h-0 max-w-[67px] self-center pt-[5px] text-center text-sm font-normal text-cove-green opacity-0"
              ref={refQuantityInput}
            >
              {cartProduct?.qty >= product.maximumSensorCountOnSite ? (
                <span className="text-cove-orange">
                  Maximum quantity reached
                </span>
              ) : (
                'System updated!'
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PKG_PRODUCTS = ['window-sticker-sm', 'CP-YS-23i-1'];
const STATIC_PRODUCTS = ['CP-PANEL-345-1', 'CPA-HUB-345-1'];
const EXCLUDED_SKUS = ['CP-YS-23i-1', 'window-sticker-sm'];

const Cart = () => {
  const cart = useCart();
  const profSetup = cart?.products?.['pro-setup'];
  const allProducts = useProducts();
  const location =
    typeof window !== 'undefined' ? window.location : { pathname: '/' };
  const { navigate } = useRouter();
  const {
    cartShowDesktop,
    productShowCart,
    sideCartDismissed,
    setProductShowCart,
    setCartShowDesktop,
  } = useContext(BaseContext);
  const { cartItemIds = [] } = cart;

  const getCartQuantity = () => {
    if (isEmpty(cart)) {
      return 0;
    }
    const count = cartItemIds.reduce((sum, sku) => {
      const product = allProducts[sku];
      const cartProduct = cart?.cartProducts?.[sku];
      if (
        !EXCLUDED_SKUS.includes(sku) &&
        cartProduct?.qty &&
        product?.productType === 'equipment'
      ) {
        sum += cartProduct?.qty;
      }
      return sum;
    }, 0);

    return count;
  };

  const cartCount = getCartQuantity() || 0;

  const getLink = () => {
    const lastCheckoutRoute = localStore.getItem('lastCheckoutRoute');
    let route = '/quiz-results';

    if (lastCheckoutRoute !== null) {
      route = lastCheckoutRoute;
    }
    return route;
  };

  useEffect(() => {
    if (productShowCart) {
      analytics.track('view_cart', {
        ecommerce: {
          currency: 'USD',
          value: cart?.totals?.totals?.orderGrandTotal,
          items: Object.keys(cart?.cartProducts).map((key) => {
            return {
              item_id: cart?.cartProducts[key]['sku'],
              item_name: cart?.cartProducts[key]['name'],
              item_price: cart?.cartProducts[key]['price'],
              item_quantity: cart?.cartProducts[key]['qty'],
            };
          }),
        },
      });
    }
  }, [
    productShowCart,
    cart?.cartProducts,
    cart?.totals?.totals?.orderGrandTotal,
  ]);

  useEffect(() => {
    const cartShowPaths = [
      '/quiz',
      '/quiz-results',
      '/cart',
      '/checkout/monitoring',
      '/checkout/customer-info',
      '/checkout/payment',
      '/checkout/order-confirmation',
      '/thanks/quiz',
      '/thanks/quiz-results',
      '/thanks/order-confirmation',
      '/thanks/checkout/monitoring',
      '/thanks/checkout/customer-info',
      '/thanks/checkout/payment',
      '/benefits/quiz',
      '/benefits/quiz-results',
      '/benefits/order-confirmation',
      '/benefits/checkout/monitoring',
      '/benefits/checkout/customer-info',
      '/benefits/checkout/payment',
    ];
    const allowPath =
      cartShowPaths.includes(location.pathname) ||
      cartShowPaths.map((p) => `${p}/`).includes(location.pathname);

    if (!allowPath && cartCount && !isMobile() && !sideCartDismissed) {
      !cartShowDesktop && setCartShowDesktop(true);
      setProductShowCart(true);
    } else {
      setProductShowCart(false);
    }
  }, [
    cartCount,
    cartShowDesktop,
    location.pathname,
    setCartShowDesktop,
    setProductShowCart,
    sideCartDismissed,
  ]);

  const totals = cart.totals?.totals;
  const formattedTotals = cart.totals?.formattedTotals || {};
  const monitoringPlan = cart?.monitoringPlan;
  const cartProducts = cart.totals?.cartProducts || [];
  const freeMonths = cart?.freeMonths;
  const totalDiscount =
    (totals?.orderRetailTotalWithTax || 0) - (totals?.orderTotalWithTax || 0);
  let freeMonitoringBasePrice = totals?.monitoringTotalWithTax || 0;
  if (freeMonths && freeMonths > 1) {
    freeMonitoringBasePrice = freeMonitoringBasePrice * freeMonths;
  }

  const freeCamera = cart.freeCamera?.();

  const getCartEquipment = () => {
    const equipment: ProductType[] = [];
    if (cart?.cartItemIds?.length) {
      orderedProductsById.forEach((sku) => {
        const product = allProducts[sku];
        const cartProduct = cart?.cartProducts?.[sku];
        if (
          cartProduct?.qty &&
          (product?.productType === 'equipment' ||
            product?.productType === 'package') &&
          !PKG_PRODUCTS.includes(sku)
        ) {
          equipment.push(product);
        }
      });
    }
    return equipment;
  };

  const renderCartProducts = (products: ProductType[], justSensors: boolean) =>
    products.map((product) => {
      if (
        (justSensors && NON_SENSORS.includes(product.sku)) ||
        (!justSensors && !NON_SENSORS.includes(product.sku))
      ) {
        return '';
      }
      return (
        <div key={product.sku}>
          <Product
            key={product.sku}
            product={product}
            handleUpdate={(item) => {
              cart.updateCartItem(item);
              if (item.qty === 0) {
                analytics.track('remove_from_cart', {
                  ecommerce: {
                    currency: 'USD',
                    value: product.price,
                  },
                });
              }
            }}
            isStatic={STATIC_PRODUCTS.includes(product.sku)}
            isFree={product.sku === freeCamera}
          />
        </div>
      );
    });

  const renderCartProductRows = () => {
    const products = getCartEquipment();
    if (products.length) {
      return [
        ...renderCartProducts(products, true),

        <div key="essentials">
          {cartProducts.length === 1 && (
            <p className="text-center">
              You do not have any sensors added to your system. Please click
              "Add products" below to add more sensors.
            </p>
          )}
        </div>,
        ...renderCartProducts(products, false),
      ];
    }
    return (
      <p className="text-center">
        You do not have any sensors added to your system. Please click "Add
        products" below to add more sensors.
      </p>
    );
  };

  return (
    <Drawer.Content
      side="right"
      wrapperClassName="max-w-[385px] bg-white"
      className="p-0 sm:p-0"
    >
      <Drawer.Close>
        <Button
          variant="unstyled"
          className="absolute right-cove-15 top-cove-15 z-10"
        >
          <Icon
            icon="Plus"
            className="size-9 translate-x-0 translate-y-0 rotate-45"
          />
          <span className="sr-only">Close Cart Drawer</span>
        </Button>
      </Drawer.Close>
      <div className="h-full w-full">
        {!isEmpty(cart.cartItemIds) ? (
          <div className="relative flex min-h-full w-full flex-col">
            <div className="w-full grow px-cove-25 py-cove-15 sm:px-[30px] sm:pt-[40px]">
              <p className="mb-cove-10 w-full border-b border-cove-dove py-cove-10 text-left text-lg font-bold">
                Your cart
              </p>
              {renderCartProductRows()}
              <div className="border-b-2 border-cove-navy-blue">
                <div className="flex items-center">
                  <img
                    className="mr-[7px] max-w-[40px] object-contain"
                    src="https://www.datocms-assets.com/10154/1657786696-headset-woman.svg"
                    alt="Cove Monitoring"
                    width={38}
                    height={49}
                  />
                  <div className="flex w-full flex-col py-cove-15">
                    <div className="flex">
                      <div className="flex flex-col">
                        <p className="mb-cove-5 flex">{monitoringPlan?.name}</p>
                        <div>
                          <p className=" m-0 text-[10px] font-bold leading-4">
                            Auto-pay ${formattedTotals.monitoringTotalWithTax}{' '}
                            on the {getDate()} of each month
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-b-2 border-cove-navy-blue" />
                {!isEmpty(profSetup) && (profSetup?.qty || 0) > 0 && (
                  <div className="flex items-center">
                    <img
                      className="mr-[7px] max-w-[40px] object-contain"
                      src={profSetup?.primaryImage?.url}
                      alt="Professional setup"
                    />
                    <div className="flex w-full flex-col py-cove-15">
                      <div className="flex">
                        <div className="flex flex-col">
                          <p className="mb-cove-5 flex">{profSetup.name}</p>
                          <div>
                            <span className=" mb-0 whitespace-nowrap text-sm font-semibold">
                              $0{' '}
                            </span>
                            <span className=" m-0 text-[10px] font-bold leading-4">
                              From ${profSetup.price}, but you won't pay until
                              installation
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <p className="w-full py-cove-15 text-center leading-[1.2em]">
                  All orders come with a yard sign and three window stickers.
                </p>
              </div>
              <div className="flex flex-col px-0 py-0">
                <div className="flex flex-wrap items-center justify-between border-b border-cove-navy-blue py-cove-15">
                  <p className="m-0 font-bold leading-none">Equipment:</p>
                  <div className="flex items-baseline justify-between">
                    <p className="relative mb-0 mr-[15px] whitespace-nowrap text-base before:absolute before:top-1/2 before:h-px before:w-full before:translate-x-0 before:translate-y-[calc(-50%-2px)] before:bg-black before:content-['']">
                      $
                      {parseFloat(
                        `${totals?.equipmentRetailTotalWithTax || 0}`,
                      ).toFixed(2)}
                    </p>
                    <p className="mb-0 font-light text-cove-green">
                      ${parseFloat(`${totals?.equipmentTotal || 0}`).toFixed(2)}
                    </p>
                  </div>
                  <div className="mb-0 mt-[2px] flex w-full justify-between text-xs font-light">
                    <div />
                    <p className="mb-0 text-xs font-light">
                      {!cart.taxCalculated
                        ? 'Tax calculated at checkout'
                        : `Including tax of: $${roundTo(
                            `${totals?.equipmentTotalTax || 0}`,
                          )}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between border-b border-cove-navy-blue py-cove-15">
                  <p className="m-0 font-bold leading-none">Monitoring:</p>
                  <div className="flex items-baseline justify-between">
                    {freeMonths ? (
                      <>
                        <span className=" relative mb-0 mr-[15px] whitespace-nowrap text-base before:absolute before:top-1/2 before:h-px before:w-full before:translate-x-0 before:translate-y-[calc(-50%-2px)] before:bg-black before:content-['']">
                          ${roundTo(`${freeMonitoringBasePrice}`, 2)}
                        </span>
                        <p className="mb-0 font-light text-cove-green">
                          {freeMonths} mo free
                        </p>
                      </>
                    ) : (
                      <p className="mb-0 font-light text-cove-green">
                        ${totals?.monitoringTotalWithTax || 0}
                      </p>
                    )}
                  </div>
                  <div className="mb-0 mt-[2px] flex w-full justify-between text-xs font-light">
                    <p
                      className="mb-0 text-xs font-light"
                      style={{ maxWidth: '125px' }}
                    >
                      Auto-pay ${totals?.monitoringTotalWithTax || 0} on the{' '}
                      {getDate()} of each month
                    </p>
                    <p className="mb-0 text-xs font-light">
                      {!cart.taxCalculated
                        ? 'Tax calculated at checkout'
                        : `Including tax of: $${roundTo(
                            `${totals?.monitoringTotalTax || 0}`,
                          )}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between border-b border-cove-navy-blue py-cove-15">
                  <p className="m-0 font-bold leading-none">Shipping:</p>
                  <div className="flex items-baseline justify-between">
                    <p className="mb-0 font-light text-cove-green">Free</p>
                  </div>
                  <p className="mb-0 mt-[2px] flex w-full justify-between text-xs font-light">
                    (Arrives in 2-10 business days)
                  </p>
                </div>
                <div className="border-b border-cove-navy-blue pt-cove-15">
                  {!!cart?.coupons?.length && <PromoCodeBreakdown />}
                  <div className="flex items-baseline justify-between">
                    <p className="m-0 font-bold leading-none">
                      Discount total:
                    </p>
                    <div className="flex items-baseline justify-between">
                      <p className="font-normal text-cove-red">
                        -<PrettyPrice price={totalDiscount} />
                      </p>
                    </div>
                  </div>
                </div>
                <PromoCodeInput />
              </div>
              <SmoothMount show={!!freeCamera}>
                <div className="flex w-full flex-wrap items-center justify-between border-none py-cove-10">
                  <p className=" m-0 font-bold leading-none">
                    Camera Credit Applied:
                  </p>
                  <div className="flex items-baseline justify-between">
                    <p
                      className="mb-0 font-light text-cove-green"
                      style={{ margin: '4px 0 0' }}
                    >
                      1 Free
                      {freeCamera ? allProducts?.[freeCamera]?.name : 'camera'}
                    </p>
                  </div>
                </div>
              </SmoothMount>
            </div>
            <div className="sticky bottom-0 flex flex-col items-center justify-center bg-cove-baby-dove px-cove-25 py-cove-15">
              <div className="mb-cove-15 flex w-full flex-wrap items-center justify-between border-none pt-0">
                <p className="m-0 font-bold leading-none">Today's total:</p>
                <div className="flex items-baseline justify-between">
                  <p className="mb-0 text-sm font-[500]">
                    ${totals?.orderTotal || 0}
                  </p>
                </div>
                <div className="mb-0 mt-[2px] flex w-full justify-between text-xs font-light">
                  <p>
                    {!cart.taxCalculated
                      ? `Including tax`
                      : `Tax calculated at checkout`}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => {
                  const route = getLink();
                  navigate(route);
                }}
              >
                Checkout
              </Button>
              <Drawer.Close>
                <a
                  href={`/quiz-results${
                    (typeof window !== `undefined` && window.location.search) ||
                    ''
                  }`}
                  className="border-none bg-transparent p-0 text-left tracking-[0.1px] text-cove-blue md:my-cove-15"
                >
                  Continue Shopping
                </a>
              </Drawer.Close>
            </div>
          </div>
        ) : (
          <div className="h-full w-full px-cove-25 py-cove-15 sm:px-[30px] sm:pt-[40px]">
            <div className="mb-cove-10 w-full border-b border-cove-dove py-cove-10 text-left text-lg font-bold">
              Your cart
            </div>
            <div className="mb-cove-25 flex items-center justify-center border-b border-b-cove-dove pb-cove-10">
              <Icon icon="Cart" /> Your cart is empty
            </div>
            <div className="flex w-full flex-col items-center gap-cove-25">
              <Button href="/quiz">Build My System</Button>
              <Button variant="outlined" href="/products">
                Shop Our Products
              </Button>
              <Button variant="outlined" href="/monitored-security">
                Learn About Monitoring
              </Button>
            </div>
          </div>
        )}
      </div>
    </Drawer.Content>
  );
};

export default Cart;
